import {
  ref, computed, watch,
} from '@vue/composition-api'
import store from '@/store'
import { format, subYears } from 'date-fns'
import {
  downloadBlob, maskCnpj, formatCurrencyInt, formatPercent, truncateString, formatCnpj,
} from '@core/utils/utils'

export default function useMonthlyLaunchIndicator() {
  const now = new Date()
  const currentYear = format(now, 'yyyy')
  const currentMonth = format(now, 'MM')

  const filterYear = ref(currentYear)
  const filterMonth = ref(currentMonth)
  const institutionId = ref(null)
  const institutionOptions = ref([])

  const loading = ref(false)
  const generalData = ref(null)
  const byInstitution = ref([])
  const byWorkplace = ref([])

  const yearOptions = (new Array(4)).fill(0).map((_, i) => {
    const year = format(subYears(now, i), 'yyyy')
    return { label: year, value: year }
  }).filter(({ value }) => value > '2020')

  const allMonthOptions = [
    { label: 'Janeiro', value: '01' },
    { label: 'Fevereiro', value: '02' },
    { label: 'Março', value: '03' },
    { label: 'Abril', value: '04' },
    { label: 'Maio', value: '05' },
    { label: 'Junho', value: '06' },
    { label: 'Julho', value: '07' },
    { label: 'Agosto', value: '08' },
    { label: 'Setembro', value: '09' },
    { label: 'Outubro', value: '10' },
    { label: 'Novembro', value: '11' },
    { label: 'Dezembro', value: '12' },
  ]

  const monthOptions = computed(() => (
    filterYear.value === currentYear
      ? allMonthOptions.filter(({ value }) => value <= currentMonth)
      : allMonthOptions
  ))

  watch(filterYear, value => {
    if (value === currentYear && filterMonth.value > currentMonth) {
      filterMonth.value = currentMonth
    }
  })

  const generalStats = computed(() => {
    if (!generalData.value) return []
    const g = generalData.value
    return [
      { label: 'Lançado', value: `${g.launched.quantity} / ${formatCurrencyInt(g.launched.value)}` },
      { label: 'Liberado', value: `${g.released.quantity} / ${formatCurrencyInt(g.released.value)}` },
      { label: 'Antecipado', value: `${g.anticipated.quantity} / ${formatCurrencyInt(g.anticipated.value)}` },
      { label: 'Limite Total', value: formatCurrencyInt(g.limit_value) },
      { label: 'Profissionais Lançado', value: g.physicians_on_duty },
      { label: 'Profissionais que Antecipou', value: g.physicians_anticipated },
      { label: '% Antecipado x Lançado', value: formatPercent(g.percentage_anticipated_over_launched) },
      { label: '% Liberado x Lançado', value: formatPercent(g.percentage_released_over_launched) },
      { label: '% Antecipado x Liberado', value: formatPercent(g.percentage_anticipated_over_released) },
      { label: '% Antecipado x Limite', value: formatPercent(g.percentage_anticipated_over_limit) },
    ]
  })

  const institutionColumns = [
    {
      key: 'institution_name', label: 'Instituição', stickyColumn: true, isRowHeader: true,
    },
    {
      key: 'launched', label: 'Lançado', formatter: v => `${v.quantity} / ${formatCurrencyInt(v.value)}`,
    },
    {
      key: 'released', label: 'Liberado', formatter: v => `${v.quantity} / ${formatCurrencyInt(v.value)}`,
    },
    {
      key: 'anticipated', label: 'Antecipado', formatter: v => `${v.quantity} / ${formatCurrencyInt(v.value)}`,
    },
    { key: 'limit_value', label: 'Limite', formatter: v => formatCurrencyInt(v) },
    { key: 'physicians_on_duty', label: 'Prof. Lançado' },
    { key: 'physicians_anticipated', label: 'Prof. Antecipou' },
    {
      key: 'percentage_anticipated_over_launched', label: 'A vs L', formatter: formatPercent,
    },
    {
      key: 'percentage_released_over_launched', label: 'Lb vs L', formatter: formatPercent,
    },
    {
      key: 'percentage_anticipated_over_released', label: 'A vs Lb', formatter: formatPercent,
    },
    {
      key: 'percentage_anticipated_over_limit', label: 'A vs Li', formatter: formatPercent,
    },
  ]

  const workplaceColumns = [
    {
      key: 'workplace_name', label: 'Unidade', stickyColumn: true, isRowHeader: true,
    },
    {
      key: 'launched', label: 'Lançado', formatter: v => `${v.quantity} / ${formatCurrencyInt(v.value)}`,
    },
    {
      key: 'released', label: 'Liberado', formatter: v => `${v.quantity} / ${formatCurrencyInt(v.value)}`,
    },
    {
      key: 'anticipated', label: 'Antecipado', formatter: v => `${v.quantity} / ${formatCurrencyInt(v.value)}`,
    },
    { key: 'payroll_value', label: 'Folha', formatter: v => formatCurrencyInt(v) },
    { key: 'physicians_on_duty', label: 'Prof. Lançado' },
    { key: 'physicians_anticipated', label: 'Prof. Antecipou' },
    {
      key: 'percentage_anticipated_over_launched', label: 'A vs L', formatter: formatPercent,
    },
    {
      key: 'percentage_released_over_launched', label: 'Lb vs L', formatter: formatPercent,
    },
    {
      key: 'percentage_anticipated_over_released', label: 'A vs Lb', formatter: formatPercent,
    },
  ]

  const getParams = () => ({
    competence: `${filterYear.value}-${filterMonth.value}`,
    institution: institutionId.value ? institutionId.value.value : undefined,
  })

  const fetchInstitutions = () => {
    store.dispatch('app-reports/fetchInstitutions', { perPage: 99999999 })
      .then(response => {
        institutionOptions.value = response.data.data.map(inst => ({
          value: inst.id,
          label: `${inst.name} | ${maskCnpj(inst.cnpj)}`,
        }))
      })
  }

  const loadReport = () => {
    loading.value = true
    store.dispatch('app-reports/monthlyLaunchIndicator', getParams())
      .then(response => {
        // eslint-disable-next-line no-param-reassign
        // response.data = {
        //   message: 'success',
        //   error: false,
        //   data: {
        //     general: {
        //       launched: {
        //         quantity: 4,
        //         value: 600000,
        //       },
        //       released: {
        //         quantity: 4,
        //         value: 600000,
        //       },
        //       anticipated: {
        //         quantity: 4,
        //         value: 600000,
        //       },
        //       limit_value: 0,
        //       physicians_on_duty: 1,
        //       physicians_anticipated: 1,
        //       percentage_anticipated_over_launched: 100,
        //       percentage_released_over_launched: 100,
        //       percentage_anticipated_over_released: 100,
        //       percentage_anticipated_over_limit: 0,
        //     },
        //     by_institution: [
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //       {
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         institution_cnpj: '83224875000102',
        //         launched: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         released: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         anticipated: {
        //           quantity: 4,
        //           value: 600000,
        //         },
        //         limit_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //         percentage_anticipated_over_limit: 0,
        //       },
        //     ],
        //     by_workplace: [
        //       {
        //         workplace_id: '13806d1b-e912-43a2-a9ca-01c540e33fb9',
        //         workplace_name: 'Alcantara e Associados',
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         launched: {
        //           quantity: 2,
        //           value: 340000,
        //         },
        //         released: {
        //           quantity: 2,
        //           value: 340000,
        //         },
        //         anticipated: {
        //           quantity: 2,
        //           value: 340000,
        //         },
        //         payroll_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //       },
        //       {
        //         workplace_id: '1974e47a-bdf0-4de0-bda9-8a094a16cd60',
        //         workplace_name: 'Upa Sim',
        //         institution_id: 'aa3a3b5d-5f47-46b6-9e60-494eb42f0a98',
        //         institution_name: 'ClienteMed',
        //         launched: {
        //           quantity: 2,
        //           value: 260000,
        //         },
        //         released: {
        //           quantity: 2,
        //           value: 260000,
        //         },
        //         anticipated: {
        //           quantity: 2,
        //           value: 260000,
        //         },
        //         payroll_value: 0,
        //         physicians_on_duty: 1,
        //         physicians_anticipated: 1,
        //         percentage_anticipated_over_launched: 100,
        //         percentage_released_over_launched: 100,
        //         percentage_anticipated_over_released: 100,
        //       },
        //     ],
        //   },
        // }
        const { general, by_institution: byInst, by_workplace: byWork } = response.data.data
        generalData.value = general
        byInstitution.value = byInst ?? []
        byWorkplace.value = byWork ?? []
      })
      .finally(() => {
        loading.value = false
      })
  }

  const downloadReport = () => {
    store.dispatch('app-reports/monthlyLaunchIndicatorExport', getParams())
      .then(response => {
        downloadBlob(null, response)
      })
  }

  return {
    loading,
    filterYear,
    filterMonth,
    yearOptions,
    monthOptions,
    institutionId,
    institutionOptions,
    fetchInstitutions,
    generalData,
    generalStats,
    byInstitution,
    byWorkplace,
    institutionColumns,
    workplaceColumns,
    loadReport,
    downloadReport,
    truncateString,
    formatCnpj,
  }
}
